import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Menu, useMenu } from '../Menu';
import { Switch } from '../Switch';
import type { DashboardLayoutApi, WidgetDef } from './useDashboardLayout';
import './DashboardLayout.scss';

/** Add widget / Lock layout / Reset to default layout, for a dashboard that uses `useDashboardLayout`. */
export function DashboardToolbar<W extends WidgetDef>({ layout }: { layout: DashboardLayoutApi<W> }) {
  const menu = useMenu();
  const { locked, hiddenWidgets } = layout;
  return (
    <div className="dash-toolbar">
      <Button
        className="action-button secondary-button"
        disabled={locked || hiddenWidgets.length === 0}
        aria-haspopup="menu"
        title={locked ? 'Unlock the layout to add widgets' : undefined}
        onClick={menu.onOpen}
      >
        <Icon name="add" outlined />
        <span>Add widget</span>
      </Button>
      <Menu anchorEl={menu.anchorEl} open={menu.open} onClose={menu.onClose} xPosition="before">
        {hiddenWidgets.map((widget) => (
          <button
            key={widget.id}
            type="button"
            role="menuitem"
            className="mat-mdc-menu-item mat-mdc-focus-indicator"
            onClick={() => layout.show(widget.id)}
          >
            <span className="mat-mdc-menu-item-text">{widget.title}</span>
          </button>
        ))}
      </Menu>
      <Button className="action-button secondary-button" disabled={locked || layout.isDefault} onClick={layout.reset}>
        <Icon name="restart_alt" outlined />
        <span>Reset to default layout</span>
      </Button>
      <Switch checked={locked} onChange={(event) => layout.setLocked(event.target.checked)}>
        Lock layout
      </Switch>
    </div>
  );
}

interface FrameProps<W extends WidgetDef> {
  widget: W;
  layout: DashboardLayoutApi<W>;
  /** The id being dragged and the handle callbacks the zone provides. */
  onDragStart: (event: DragEvent<HTMLElement>, id: string) => void;
  onDragEnd: () => void;
  dropEdge: 'before' | 'after' | null;
  dragging: boolean;
  wide: boolean;
  children: ReactNode;
}

function WidgetFrame<W extends WidgetDef>({
  widget,
  layout,
  onDragStart,
  onDragEnd,
  dropEdge,
  dragging,
  wide,
  children,
}: FrameProps<W>) {
  const frame = useRef<HTMLDivElement>(null);
  const { locked } = layout;
  const className = [
    'dash-widget',
    wide && 'dash-widget--wide',
    dragging && 'dash-widget--dragging',
    dropEdge && `dash-widget--drop-${dropEdge}`,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div ref={frame} className={className} data-widget-id={widget.id}>
      {!locked && (
        <div className="dash-widget-tools" role="group" aria-label={`${widget.title} layout`}>
          <span
            className="dash-widget-handle"
            draggable
            title="Drag to rearrange"
            aria-hidden="true"
            data-testid={`drag-${widget.id}`}
            onDragStart={(event) => {
              if (frame.current) event.dataTransfer?.setDragImage?.(frame.current, 12, 12);
              onDragStart(event, widget.id);
            }}
            onDragEnd={onDragEnd}
          >
            <Icon name="drag_indicator" />
          </span>
          <button
            type="button"
            aria-label={`Move ${widget.title} up`}
            title="Move up"
            disabled={!layout.canShift(widget.id, -1)}
            onClick={() => layout.shift(widget.id, -1)}
          >
            <Icon name="arrow_upward" />
          </button>
          <button
            type="button"
            aria-label={`Move ${widget.title} down`}
            title="Move down"
            disabled={!layout.canShift(widget.id, 1)}
            onClick={() => layout.shift(widget.id, 1)}
          >
            <Icon name="arrow_downward" />
          </button>
          {widget.removable && (
            <button
              type="button"
              className="dash-widget-remove"
              aria-label={`Remove ${widget.title}`}
              title="Remove widget"
              onClick={() => layout.hide(widget.id)}
            >
              <Icon name="close" />
            </button>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

interface ZonesProps<W extends WidgetDef> {
  layout: DashboardLayoutApi<W>;
  /** Class per zone, for the grid each zone uses. */
  zoneClassName?: Record<string, string>;
  /** Widgets that span the whole zone width. */
  wideIds?: readonly string[];
  /** The widget's content (its own heading included). */
  renderWidget: (widget: W) => ReactNode;
}

interface DropTarget {
  zone: string;
  index: number;
  /** The widget the marker is drawn on, and which edge; null when dropping at the end of an empty zone. */
  overId: string | null;
  edge: 'before' | 'after';
}

/**
 * Renders the zones of a `useDashboardLayout` dashboard and lets a widget be dragged (HTML5 drag and drop, from its
 * handle) to another place or zone. The keyboard does the same through the move up / move down buttons.
 */
export function DashboardZones<W extends WidgetDef>({
  layout,
  zoneClassName = {},
  wideIds = [],
  renderWidget,
}: ZonesProps<W>) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [target, setTarget] = useState<DropTarget | null>(null);
  const dragRef = useRef<string | null>(null);

  const startDrag = (event: DragEvent<HTMLElement>, id: string) => {
    if (layout.locked) {
      event.preventDefault();
      return;
    }
    dragRef.current = id;
    setDragId(id);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      try {
        event.dataTransfer.setData('text/plain', id);
      } catch {
        // some browsers refuse data on synthetic events
      }
    }
  };

  const endDrag = () => {
    dragRef.current = null;
    setDragId(null);
    setTarget(null);
  };

  const locateTarget = (event: DragEvent<HTMLElement>, zone: string): DropTarget => {
    const ids = layout.zoneWidgets(zone).map((widget) => widget.id);
    const over = (event.target as HTMLElement).closest?.('[data-widget-id]') as HTMLElement | null;
    const overId = over?.dataset.widgetId;
    if (over && overId && ids.includes(overId)) {
      const box = over.getBoundingClientRect();
      const after = box.width > 0 && (event.clientX - box.left) / box.width > 0.5;
      const at = ids.indexOf(overId);
      return { zone, index: after ? at + 1 : at, overId, edge: after ? 'after' : 'before' };
    }
    const last = ids[ids.length - 1] ?? null;
    return { zone, index: ids.length, overId: last, edge: 'after' };
  };

  return (
    <>
      {layout.zones.map((zone) => {
        const widgets = layout.zoneWidgets(zone);
        if (widgets.length === 0 && !dragId) return null;
        return (
          <div
            key={zone}
            className={`dash-zone ${zoneClassName[zone] ?? ''}${dragId ? ' dash-zone--receiving' : ''}`.trim()}
            data-zone={zone}
            onDragOver={(event) => {
              if (!dragRef.current || layout.locked) return;
              event.preventDefault();
              if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
              setTarget(locateTarget(event, zone));
            }}
            onDrop={(event) => {
              const id = dragRef.current ?? event.dataTransfer?.getData('text/plain');
              if (!id || layout.locked) return;
              event.preventDefault();
              const drop = locateTarget(event, zone);
              layout.move(id, zone, drop.index);
              endDrag();
            }}
          >
            {widgets.length === 0 && <div className="dash-zone-empty">Drop a widget here</div>}
            {widgets.map((widget) => (
              <WidgetFrame
                key={widget.id}
                widget={widget}
                layout={layout}
                onDragStart={startDrag}
                onDragEnd={endDrag}
                dragging={dragId === widget.id}
                dropEdge={target && target.overId === widget.id && dragId !== widget.id ? target.edge : null}
                wide={wideIds.includes(widget.id)}
              >
                {renderWidget(widget)}
              </WidgetFrame>
            ))}
          </div>
        );
      })}
    </>
  );
}
